/**
 * TVアンケート第1回・第2回を Sheets 上で結合するための匿名回答連結ID。
 * 同一ブラウザ・同一 localStorage・同一 campaign・有効期限内のみ再利用する。
 * 氏名・IP・端末固有ID等は扱わない。第3回（外部 Form）には使わない。
 */

const RESPONSE_ID_KEY = 'tv_survey_response_id';
const RESPONSE_CAMPAIGN_KEY = 'tv_survey_response_campaign';
const RESPONSE_CREATED_AT_KEY = 'tv_survey_response_created_at';

const EXPIRY_DAYS = 7;
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

export const TV_SURVEY_RESPONSE_STORAGE_KEYS = {
  id: RESPONSE_ID_KEY,
  campaign: RESPONSE_CAMPAIGN_KEY,
  createdAt: RESPONSE_CREATED_AT_KEY,
};

function getLocalStorage() {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readKey(store, key) {
  if (!store) return '';
  try {
    return store.getItem(key) || '';
  } catch {
    return '';
  }
}

function writeKey(store, key, value) {
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    // private mode など
  }
}

export function normalizeTvSurveyCampaign(campaign) {
  const value = campaign == null ? '' : String(campaign).trim();
  return value || 'unknown';
}

export function isTvSurveyResponseIdExpired(createdAt, nowMs = Date.now()) {
  if (createdAt == null || createdAt === '') return true;
  const createdAtMs = Number(createdAt);
  if (!Number.isFinite(createdAtMs)) return true;
  return nowMs - createdAtMs > EXPIRY_MS;
}

function createUuidV4() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // フォールバック（主要ブラウザでは randomUUID がある想定）
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * @param {string} [campaign] utm_campaign（空なら unknown）
 * @param {{ storage?: Storage | null, nowMs?: number }} [options] テスト用に storage / now を差し替え可能
 * @returns {string} UUID v4。localStorage 不可時は都度新規（永続化なし）
 */
export function getOrCreateTvSurveyResponseId(campaign, options = {}) {
  const store = options.storage !== undefined ? options.storage : getLocalStorage();
  const nowMs = options.nowMs != null ? options.nowMs : Date.now();
  const normalizedCampaign = normalizeTvSurveyCampaign(campaign);

  const storedId = readKey(store, RESPONSE_ID_KEY);
  const storedCampaign = readKey(store, RESPONSE_CAMPAIGN_KEY);
  const createdAt = readKey(store, RESPONSE_CREATED_AT_KEY);

  const canReuse =
    Boolean(storedId)
    && storedCampaign === normalizedCampaign
    && !isTvSurveyResponseIdExpired(createdAt, nowMs);

  if (canReuse) {
    return storedId;
  }

  const newId = createUuidV4();
  writeKey(store, RESPONSE_ID_KEY, newId);
  writeKey(store, RESPONSE_CAMPAIGN_KEY, normalizedCampaign);
  writeKey(store, RESPONSE_CREATED_AT_KEY, String(nowMs));
  return newId;
}
